import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled, { css } from 'styled-components';

import { useLocalize } from '@treams/treams-hooks';
import { EvaluationSectionItemType, EvaluationSubmission } from '@treams/treams-network';
import {
	Button,
	Callout,
	Checkbox,
	CircleCheckmarkIcon,
	FlexGap,
	Paragraph,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import { useAIInsightsContext } from '../../contexts/AIInsightsContext';

const SignLine = styled(Flex)<{ $flash: boolean }>`
	width: 100%;
	box-sizing: border-box;
	background: ${tokens.surface.white};
	border: 1px solid ${tokens.border.default};
	padding: ${tokens.padding.compact}px ${tokens.padding.container}px;
	border-radius: ${tokens.radius.sm}px;
	gap: ${tokens.gap.list}px;

	@keyframes flashBorder {
		0%,
		100% {
			background: ${tokens.surface.white};
			border: 1px solid ${tokens.border.default};
		}
		50% {
			background: ${tokens.surface.secondary};
			border: 1px solid ${tokens.border.focus};
		}
	}

	${(props) =>
		props.$flash &&
		css`
			animation: flashBorder 0.4s ease-in-out 2 400ms;
		`}
`;

function useSignatureTitle(submission: EvaluationSubmission): string | undefined {
	const { localize } = useLocalize();
	return submission.evaluationForm.sections.reduce((result, section) => {
		if (result) {
			return result;
		}

		const signatureItem = section.items.find(
			(item) => item.type === EvaluationSectionItemType.Signature,
		);

		return localize(signatureItem?.signature?.title)?.text;
	}, undefined);
}

export default function ConfirmReminderCallout(props: {
	submission: EvaluationSubmission;
	saving: boolean;
	flashSignSection: boolean;
	signChecked: boolean;
	setSignChecked: (value: boolean) => void;
	setShowSignModal: (value: boolean) => void;
}) {
	const { t } = useTranslation();

	const signatureTitle = useSignatureTitle(props.submission);

	const { showAIInsightsFeedback } = useAIInsightsContext();

	return (
		<Callout color="blue" mb={3}>
			<Box width="100%">
				<Box mb={2}>
					<Subheading>{t('conversations.confirm.reminder.title')}</Subheading>
					<Paragraph>{signatureTitle || t('conversations.confirm.reminder.description')}</Paragraph>
				</Box>
				<SignLine
					alignItems={['flex-start', 'flex-start', 'center']}
					$flash={props.flashSignSection}
					flexDirection={['column', 'column', 'row']}
				>
					<Box flex={1} minWidth={0}>
						<FlexGap alignItems="center" gap={tokens.gap.list}>
							<Checkbox
								checked={props.signChecked}
								onClick={() => props.setSignChecked(!props.signChecked)}
							/>
							<Box flex={1} minWidth={0} onClick={() => props.setSignChecked(!props.signChecked)}>
								<Paragraph>
									{showAIInsightsFeedback
										? t('conversations.confirm.reminder.disclaimer.including.aiInsights')
										: t('conversations.confirm.reminder.disclaimer')}
								</Paragraph>
							</Box>
						</FlexGap>
					</Box>
					<Box flexShrink={0}>
						<Button
							type="primary"
							icon={<CircleCheckmarkIcon />}
							title={t('conversations.confirm.reminder.button')}
							small
							onClick={() => props.setShowSignModal(true)}
							disabled={!props.signChecked}
						/>
					</Box>
				</SignLine>
			</Box>
		</Callout>
	);
}
